import "./addressSearch.scss";
import { ComponentRegistry } from "@ts/utils/ComponentRegistry";
import { debounce } from "@ts/utils/";
export interface AddressSearchOptions {
    apiKey: string;
    placeholder?: string;
    lookupAddressUrl: string;
    validationEndpoint?: string;
    suggestionDelay?: number;
    submitButtonSelector?: string;
}

interface AddressSuggestion {
    street_line: string;
    secondary: string;
    city: string;
    state: string;
    zipcode: string;
    entries: number;
}

interface AddressSelectedData {
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    entries?: string;
}
interface AddressRestrictionsValidationResult {
    isValid: boolean;
    validationFailures?: string[];
}

type AddressSelectHook = (data: AddressSelectedData, input: HTMLInputElement) => void | Promise<void>;
type AddressOpenHook = (input: HTMLInputElement) => void | Promise<void>;
type AddressValidationFailure = {
    kind: "http" | "network" | "configuration";
    response?: Response;
    status?: number;
    statusText?: string;
    error?: unknown;
    bodyText?: string;
};

type AddressValidationSuccess = {
    isValid: boolean;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
};

type AddressValidationError = {
    title: string;
    status: number;
    errors?: Array<{ name: string; reason: string }>;
};

type AddressValidationResult = AddressValidationSuccess | AddressValidationError;
type ValidateAddressOptions = {
    onFail?: (failure: AddressValidationFailure) => void | Promise<void>;
};


export class AddressSearch {
    private static registry = new ComponentRegistry<AddressSearch>("address-search");

    private readonly id: string;
    private readonly input: HTMLInputElement;
    private readonly options: AddressSearchOptions;
    private readonly suggestionDelay: number;
    private readonly addressSuggestionMenuClass = "address-suggestions";
    private readonly addressSuggestionContainerClass = "address-search-wrapper";
    private readonly submitButton: HTMLButtonElement | null;

    private selectCallback: AddressSelectHook = () => {};
    private openCallback: AddressOpenHook = () => {};
    private processSuggestionRequest: (query: string) => void;

    private constructor(input: HTMLInputElement, options: AddressSearchOptions) {
        this.id = AddressSearch.registry.register(input, this);
        this.input = input;
        this.options = options;
        this.suggestionDelay = options.suggestionDelay ?? 300;

        if (options.placeholder) {
            this.input.placeholder = options.placeholder;
        }
        
        if (options.submitButtonSelector) {
            this.submitButton = document.querySelector<HTMLButtonElement>(options.submitButtonSelector);
        } else {
            const form = this.input.closest("form");
            this.submitButton = form?.querySelector<HTMLButtonElement>("button[type='submit']") ?? null;
        }

        this.processSuggestionRequest = debounce(
            (query: string) => this.getSuggestions(query),
            this.suggestionDelay
        );

        this.initialize();
    }

    private initialize(): void {
        this.input.addEventListener("keydown", this.handleKeyDown);
        this.input.addEventListener("input", this.handleInput);
        this.input.addEventListener("click", this.handleClick);

        this.buildAddressMenu();
        this.buildAutoCompleteBypass();
    }

    private handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key === "Enter") {
            event.preventDefault();
        }
    };

    private handleClick = (e: MouseEvent): void => {
        // prevent form submit / default when needed
        e.preventDefault();
    };

    private handleInput = (event: Event): void => {
        const input = event.target as HTMLInputElement;
        const searchQuery = input.value;

        if (searchQuery) {
            this.showAddressMenu();
            this.searchOpen();
            this.processSuggestionRequest(searchQuery);
        } else {
            this.hideAddressMenu();
        }
    };

    private buildAutoCompleteBypass(): void {
        const hiddenInput = document.createElement("input");
        hiddenInput.style.display = "none";
        hiddenInput.autocomplete = "address-hidden-field" as any;

        const wrapper = this.input.closest(`.${this.addressSuggestionContainerClass}`);
        if (wrapper) {
            wrapper.insertBefore(hiddenInput, wrapper.firstChild);
        }
    }

    private buildAddressMenu(): void {
        const menu = document.createElement("ul");
        menu.classList.add(this.addressSuggestionMenuClass);
        this.input.insertAdjacentElement("afterend", menu);
    }

    private getMenu(): HTMLElement | null {
        const parentWrapper = this.input.closest(`.${this.addressSuggestionContainerClass}`);
        if (!parentWrapper) return null;

        return parentWrapper.querySelector<HTMLElement>(`.${this.addressSuggestionMenuClass}`);
    }

    private showAddressMenu(): void {
        const menu = this.getMenu();
        if (menu) {
            menu.style.display = "block";
        }
    }

    private hideAddressMenu(): void {
        const menu = this.getMenu();
        if (menu) {
            menu.style.display = "none";
        }
    }

    private buildAddress(suggestion: AddressSuggestion): string {
        const entryCountClass = suggestion.entries > 1 ? "multi-entry" : "single-entry";
        const additionalEntries =
            suggestion.entries > 1
                ? `<span class='additional-entries-found'>+ ${suggestion.entries} more <i class='fas fa-chevron-right'></i></span>`
                : "";

        let address = `<span class='suggested-entry ${entryCountClass}'><span class='primary-entry'>${suggestion.street_line} ${suggestion.secondary} ${suggestion.city}, ${suggestion.state} ${suggestion.zipcode}</span> ${additionalEntries}</span>`;

        const inputAddress = this.input.value;
        for (let i = 0; i < address.length; i++) {
            const theLettersMatch =
                typeof inputAddress[i] === "undefined" ||
                address[i].toLowerCase() !== inputAddress[i].toLowerCase();

            if (theLettersMatch) {
                address = [address.slice(0, i), "<strong>", address.slice(i)].join("");
                break;
            }
        }

        return address;
    }

    private displaySuggestions = (suggestions: AddressSuggestion[]): void => {
        const menu = this.getMenu();
        if (!menu) return;

        menu.innerHTML = "";

        suggestions.forEach(suggestion => {
            const listItem = document.createElement("li");
            const div = document.createElement("div");

            div.innerHTML = this.buildAddress(suggestion);
            div.addEventListener("click", this.handleSelection);

            div.dataset.address1 = suggestion.street_line;
            div.dataset.address2 = suggestion.secondary;
            div.dataset.city = suggestion.city;
            div.dataset.state = suggestion.state;
            div.dataset.zip = suggestion.zipcode;
            div.dataset.entries = suggestion.entries.toString();

            listItem.appendChild(div);
            menu.appendChild(listItem);
        });
    };

    private handleSelection = (e: Event): void => {
        const selection = e.currentTarget as HTMLElement;
        const ds = selection.dataset;

        const data: AddressSelectedData = {
            address1: ds.address1 ?? "",
            address2: ds.address2 ?? "",
            city: ds.city ?? "",
            state: ds.state ?? "",
            zip: ds.zip ?? "",
            entries: ds.entries,
        };

        const { address1, address2, entries, city, state, zip } = data;
        const numOfUnits = parseInt(entries ?? "0", 10);

        if (numOfUnits > 1) {
            this.input.value = `${address1} `;
            const selected = `${address1} ${address2} (${entries}) ${city} ${state} ${zip}`;
            this.getSuggestions(address1, selected);
        } else {
            this.hideAddressMenu();
            this.selectSuccess(data);
        }
    };

    private getSuggestions(query: string, selected?: string): void {
        const excludeStates = "AS;FM;GU;MH;MP;PR;PW;VI";
        const searchUrl =
            `${this.options.lookupAddressUrl}` +
            `key=${this.options.apiKey}` +
            `&search=${encodeURIComponent(query)}` +
            `&exclude_states=${encodeURIComponent(excludeStates)}` +
            (selected ? `&selected=${encodeURIComponent(selected)}` : "");

        fetch(searchUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                if (data?.suggestions) {
                    this.displaySuggestions(data.suggestions as AddressSuggestion[]);
                }
            })
            .catch(error => {
                // eslint-disable-next-line no-console
                console.error(error);
            });
    }

    private selectSuccess(data: AddressSelectedData): void {
        this.setValidateAddressClass();
        this.selectCallback(data, this.input);
    }

    private setValidateAddressClass(): void {
        this.submitButton?.classList.add("validate-address");
    }

    private clearValidateAddressClass(): void {
        this.submitButton?.classList.remove("validate-address");
    }

    private searchOpen(): void {
        this.openCallback(this.input);
    }

    public onSelect(callback: AddressSelectHook): AddressSearch {
        this.selectCallback = callback;
        return this;
    }

    public onOpen(callback: AddressOpenHook): AddressSearch {
        this.openCallback = callback;
        return this;
    }

    public destroy = (): void => {
        this.input.removeEventListener("keydown", this.handleKeyDown);
        this.input.removeEventListener("input", this.handleInput);
        this.input.removeEventListener("click", this.handleClick);
        AddressSearch.registry.unregister(this.id);
    };

    // ----------------------------------------------------------------
    // Static utility methods 
    // ----------------------------------------------------------------

     // Staic Methods

    public static create(
        target: | string | HTMLInputElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: AddressSearchOptions
    ): AddressSearch[] | null {
        const inputs = ComponentRegistry.processTargets<AddressSearch, HTMLElement>(
            AddressSearch.registry,
            target,
            [HTMLInputElement]
        ) as HTMLInputElement[];

        if (!inputs.length) return null;

        return inputs.map(input => new AddressSearch(input, options));
    }

    public static getById(id: string): AddressSearch | null {
        return AddressSearch.registry.get(id) ?? null;
    }

    private static async handleValidateError(response, options) {
        let errorBody: AddressValidationError;
        let bodyText: string | undefined;

        try {
            errorBody = await response.json();
        } catch {
            bodyText = await response.text().catch(() => undefined);
        }

        options.onFail?.({
            title: errorBody?.title ?? "Address validation failed",
            status: errorBody.status ?? response.status,
            errors: errorBody.errors,
        });
    }

    public static async validateAddress(
        validationEndpoint: string,
        token: string,
        addressData: AddressSelectedData,
        options?: ValidateAddressOptions
    ): Promise<AddressValidationResult | false> {

        if (!validationEndpoint) return false;

        try {
            const response = await fetch(validationEndpoint, {
                method: "POST",
                headers: { 
                    "Content-Type": "application/json", 
                    "RequestVerificationToken": token
                },
                body: JSON.stringify(addressData),
            });

            if (!response.ok) {
                this.handleValidateError(response, options);
                return false;
            }

            const result: AddressValidationResult = await response.json();
            return result;
        } catch (error) {
            console.error("Address validation error:", error);
            return false;
        }
    }
}